import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faBoxes,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWeightHanging,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faPalette,
    faRulerCombined,
    faBoxes as faBoxesIcon,
    faInfoCircle,
    faHistory,
    faStore,
    faExchangeAlt,
    faShoppingCart,
    faTruck,
    faShip,
    faRoad,
    faMoneyBillWave,
    faUniversity,
    faMoneyCheck,
    faCreditCard,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faGlobe,
    faClock,
    faCalendarCheck,
    faFileInvoice,
    faReceipt,
    faMoneyBill,
    faDownload as faDownloadIcon,
    faHourglassHalf,
    faCheckDouble,
    faMapMarkedAlt,
    faFlag,
    faClock as faClockIcon,
    faIdCard,
    faAddressCard,
    faComment,
    faCamera,
    faImage,
    faPhotoVideo,
    faExpand,
    faCompress,
    faArrowsAlt,
    faMinus,
    faPlus as faPlusIcon
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance for container list
const createContainerListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_container_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Container List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_container_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        containerList: 'Containers List',
        allContainers: 'My Containers',
        containers: 'Containers',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search containers...',
        refresh: 'Refresh',
        loadingContainers: 'Loading containers...',
        noContainersFound: 'No containers found',
        containerDetails: 'Container Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        containerNo: 'Container No',
        containerType: 'Container Type',
        vehicleNo: 'Vehicle No',
        driverName: 'Driver Name',
        origin: 'Origin',
        destination: 'Destination',
        dispatchDate: 'Dispatch Date',
        arrivalDate: 'Arrival Date',
        status: 'Status',
        remarks: 'Remarks',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        pending: 'Pending',
        inTransit: 'In Transit',
        arrived: 'Arrived',
        delayed: 'Delayed',
        completed: 'Completed',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        viewPicture: 'Picture',
        deleteConfirm: 'Are you sure you want to delete this container?',
        deletedSuccess: 'Container deleted successfully',
        deleteError: 'Error deleting container',
        networkError: 'Network error occurred',
        containerId: 'Container ID',
        routeInfo: 'Route Information',
        driverInfo: 'Driver Information',
        timelineInfo: 'Timeline Information',
        notProvided: 'Not provided',
        pendingBadge: 'Pending',
        inTransitBadge: 'In Transit',
        arrivedBadge: 'Arrived',
        delayedBadge: 'Delayed',
        completedBadge: 'Completed',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        containerNoFilter: 'Container Number',
        vehicleNoFilter: 'Vehicle Number',
        driverNameFilter: 'Driver Name',
        statusFilter: 'Status',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        addedByFilter: 'Added By',
        originFilter: 'Origin',
        destinationFilter: 'Destination',
        editContainer: 'Edit Container',
        updateContainer: 'Update Container',
        updating: 'Updating...',
        containerUpdated: 'Container updated successfully',
        updateError: 'Error updating container',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the container details below',
        containerNoRequired: 'Container number is required',
        invalidDate: 'Please enter a valid date',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this container?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'My Records Only',
        loadingUserData: 'Loading your container data...',
        noUserRecords: 'No containers found in this shop',
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        containerReport: 'Container Report',
        containerSummary: 'Container Summary',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalContainers: 'Total Containers',
        pendingAmount: 'Pending Count',
        inTransitCount: 'In Transit',
        arrivedCount: 'Arrived',
        delayedCount: 'Delayed',
        completedCount: 'Completed',
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        generatingPDF: 'Generating PDF...',
        pdfGenerationError: 'Error generating PDF',
        pdfGenerated: 'PDF generated successfully',
        reportFilters: 'Applied Filters',
        reportDate: 'Report Date',
        dispatch: 'Dispatch',
        arrival: 'Arrival',
        daysInTransit: 'Days in Transit',
        from: 'From',
        to: 'To',
        createFirstContainer: 'Create Your First Container',
        addNewContainer: 'Add New Container',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        conveyancePicture: 'Conveyance Picture',
        noPicture: 'No picture available'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const containerListTranslator = createContainerListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 400, label: 'Narrow', icon: faMinus },
    medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 850, label: 'Wide', icon: faPlusIcon },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

// Main Component
const ContainerList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [containers, setContainers] = useState([]);
    const [filteredContainers, setFilteredContainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [containerToDelete, setContainerToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // PDF Options State - Direct in row
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        container_no: '',
        container_type: '',
        vehicle_no: '',
        driver_name: '',
        origin: '',
        destination: '',
        dispatch_date: '',
        arrival_date: '',
        status: '',
        remarks: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#8b5cf6';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterBy, setFilterBy] = useState('all');
    const [containerNoFilter, setContainerNoFilter] = useState('');
    const [vehicleNoFilter, setVehicleNoFilter] = useState('');
    const [driverNameFilter, setDriverNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [originFilter, setOriginFilter] = useState('');
    const [destinationFilter, setDestinationFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Status options for dropdown
    const statusOptions = useMemo(() => [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'in_transit', label: 'In Transit' },
        { value: 'arrived', label: 'Arrived' },
        { value: 'delayed', label: 'Delayed' },
        { value: 'completed', label: 'Completed' }
    ], []);

    // Container type options for display
    const containerTypeOptions = useMemo(() => [
        '20ft', '40ft', '40ft HC', '45ft', '20ft Reefer', '40ft Reefer',
        '20ft Open Top', '40ft Open Top', '20ft Flat Rack', '40ft Flat Rack',
        'Tank Container', 'Other'
    ], []);

    // Memoized calculations
    const totals = useMemo(() => ({
        total: filteredContainers.length,
        pending: filteredContainers.filter(c => c.status === 'pending').length,
        inTransit: filteredContainers.filter(c => c.status === 'in_transit').length,
        arrived: filteredContainers.filter(c => c.status === 'arrived').length,
        delayed: filteredContainers.filter(c => c.status === 'delayed').length,
        completed: filteredContainers.filter(c => c.status === 'completed').length
    }), [filteredContainers]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredContainers.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentContainers = filteredContainers.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentContainers };
    }, [filteredContainers, currentPage, itemsPerPage]);

    // Function to show toast
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer on component unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        let isSubscribed = true;
        
        const loadTranslations = async () => {
            const trans = await containerListTranslator.getTranslations(currentLanguage);
            if (isSubscribed) {
                setTranslations(trans);
            }
        };
        
        loadTranslations();
        
        return () => {
            isSubscribed = false;
        };
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'rtl';
                table.style.textAlign = 'right';
            });
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'ltr';
                table.style.textAlign = 'left';
            });
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDragging(true);
        
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!modalRef.current) return;
        
        setIsResizing(true);
        resizeType.current = type;
        
        const rect = modalRef.current.getBoundingClientRect();
        
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 350,
            minHeight: 400,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                
                switch (resizeType.current) {
                    case 'e':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        break;
                    case 'w':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'n':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'sw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                }
                
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        
        if (!showEditModal) {
            setIsModalInitialized(false);
        }
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Fetch containers on mount and when shop changes
    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchContainers();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
            if (isMountedRef.current) {
                showToast('info', translations.noShopSelected || 'Please select a shop first');
            }
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id]);

    // Apply filters with debounce
    const applyFilters = useCallback(() => {
        let filtered = [...containers];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(container =>
                container.container_no?.toLowerCase().includes(term) ||
                container.container_type?.toLowerCase().includes(term) ||
                container.vehicle_no?.toLowerCase().includes(term) ||
                container.driver_name?.toLowerCase().includes(term) ||
                container.origin?.toLowerCase().includes(term) ||
                container.destination?.toLowerCase().includes(term) ||
                container.remarks?.toLowerCase().includes(term) ||
                container.created_by_username?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'containerNo' && containerNoFilter.trim()) {
            const term = containerNoFilter.toLowerCase();
            filtered = filtered.filter(container =>
                container.container_no?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'vehicleNo' && vehicleNoFilter.trim()) {
            const term = vehicleNoFilter.toLowerCase();
            filtered = filtered.filter(container =>
                container.vehicle_no?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'driverName' && driverNameFilter.trim()) {
            const term = driverNameFilter.toLowerCase();
            filtered = filtered.filter(container =>
                container.driver_name?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'status' && statusFilter) {
            filtered = filtered.filter(container =>
                container.status === statusFilter
            );
        }

        if (filterBy === 'origin' && originFilter.trim()) {
            const term = originFilter.toLowerCase();
            filtered = filtered.filter(container =>
                container.origin?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'destination' && destinationFilter.trim()) {
            const term = destinationFilter.toLowerCase();
            filtered = filtered.filter(container =>
                container.destination?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(container =>
                container.created_by_username?.toLowerCase().includes(term)
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(container => {
                if (!container.dispatch_date) return false;
                const dispatchDate = new Date(container.dispatch_date);
                dispatchDate.setHours(0, 0, 0, 0);
                return dispatchDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(container => {
                if (!container.dispatch_date) return false;
                const dispatchDate = new Date(container.dispatch_date);
                return dispatchDate <= end;
            });
        }

        setFilteredContainers(filtered);
        setCurrentPage(1);
    }, [containers, searchTerm, filterBy, containerNoFilter, vehicleNoFilter, driverNameFilter, 
        statusFilter, originFilter, destinationFilter, addedByFilter, startDate, endDate]);

    // Apply filters when dependencies change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Fetch available shops for switching
    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) {
                setAvailableShops(shopsData.shops || []);
            }
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) {
                setAvailableShops([]);
            }
        } finally {
            if (isMountedRef.current) {
                setLoadingShops(false);
            }
        }
    }, [user, getShopsForSwitch]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast]);

    // Fetch business info
    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '',
                        email: data.email || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || '',
                        bussinesslogo: '',
                        businessaddress: '',
                        fullname: user.fullname || '',
                        email: user.email || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        }
    }, [user]);

    const fetchContainers = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available. Please login again.');
                setLoading(false);
            }
            return;
        }

        if (!currentShop) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }

        if (isMountedRef.current) {
            setLoading(true);
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/containers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: {}
                })
            });

            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setContainers(data.data || []);
                    resetFilters();

                    if (data.data.length === 0) {
                        showToast('info', translations.noUserRecords || 'No containers found in this shop');
                    }
                } else {
                    if (data.message.includes('not assigned to any shop')) {
                        showToast('error', 'You are not assigned to any shop. Please contact administrator.');
                    } else {
                        showToast('error', data.message || translations.networkError || 'Network error occurred');
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching containers:', error);
            if (isMountedRef.current) {
                showToast('error', translations.networkError || 'Network error occurred');
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [user, currentShop, translations, showToast]);

    const resetFilters = useCallback(() => {
        setFilterBy('all');
        setContainerNoFilter('');
        setVehicleNoFilter('');
        setDriverNameFilter('');
        setStatusFilter('');
        setOriginFilter('');
        setDestinationFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    const handleSearch = useCallback(() => {
        applyFilters();
    }, [applyFilters]);

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Format date for PDF
    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    // Get current date for PDF
    const currentDateGlobal = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Get status badge style for PDF
    const getStatusBadgeStyle = (status) => {
        switch (status) {
            case 'pending':
                return { backgroundColor: '#fef3c7', color: '#b45309' };
            case 'in_transit':
                return { backgroundColor: '#dbeafe', color: '#1e40af' };
            case 'arrived':
                return { backgroundColor: '#d1fae5', color: '#065f46' };
            case 'delayed':
                return { backgroundColor: '#fee2e2', color: '#991b1b' };
            case 'completed':
                return { backgroundColor: '#e9d5ff', color: '#5b21b6' };
            default:
                return { backgroundColor: '#f3f4f6', color: '#374151' };
        }
    };

    // Get status label for PDF
    const getStatusLabelForPDF = (status) => {
        switch (status) {
            case 'pending':
                return translations.pending || 'Pending';
            case 'in_transit':
                return translations.inTransit || 'In Transit';
            case 'arrived':
                return translations.arrived || 'Arrived';
            case 'delayed':
                return translations.delayed || 'Delayed';
            case 'completed':
                return translations.completed || 'Completed';
            default:
                return status?.replace('_', ' ') || '-';
        }
    };

    // Calculate rows per page based on orientation and page size
    const getRowsPerPage = useCallback(() => {
        if (pdfPageSize === 'a5') return pdfOrientation === 'portrait' ? 18 : 20;
        if (pdfPageSize === 'legal') return pdfOrientation === 'portrait' ? 32 : 35;
        if (pdfPageSize === 'letter') return pdfOrientation === 'portrait' ? 28 : 30;
        return pdfOrientation === 'portrait' ? 25 : 27;
    }, [pdfOrientation, pdfPageSize]);

    // Enhanced PDF Export Function with direct options
    const exportToPDF = useCallback(async () => {
        if (!filteredContainers || filteredContainers.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        setPdfLoading(true);

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#8b5cf6';
            
            const pageSizeMap = {
                'a4': 'a4',
                'letter': 'letter',
                'legal': 'legal',
                'a5': 'a5'
            };
            
            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
            
            const pdf = new jsPDF({
                orientation: pdfOrientation,
                unit: 'mm',
                format: pageFormat,
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const rtlTextStyles = isRTL ? `
                unicode-bidi: plaintext;
                text-rendering: optimizeLegibility;
                font-kerning: normal;
                font-variant-ligatures: common-ligatures;
                font-feature-settings: "calt", "liga", "kern";
                letter-spacing: 0;
                word-spacing: normal;
            ` : '';

            const headerCellStyle = `
                padding: 10px 8px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: ${pdfOrientation === 'portrait' ? '10px' : '9px'}; 
                letter-spacing: ${isRTL ? '0' : '0.3px'};
                border-right: 1px solid rgba(255,255,255,0.1);
                ${rtlTextStyles}
            `;

            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');

                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                const r = parseInt(themeColor[0]);
                const g = parseInt(themeColor[1]);
                const b = parseInt(themeColor[2]);

                pdfDoc.setFillColor(r, g, b);
                pdfDoc.setDrawColor(r, g, b);
                pdfDoc.setLineWidth(0.2);

                const dotSize = 0.3;
                const dotSpacing = 8;

                for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                    for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                        pdfDoc.circle(x, y, dotSize, 'F');
                    }
                }

                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            const rowsPerPage = getRowsPerPage();
            
            let containersToExport = [...filteredContainers];
            
            if (pdfPageRange === 'range') {
                const totalPages = Math.ceil(containersToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPages, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, containersToExport.length);
                containersToExport = containersToExport.slice(startIdx, endIdx);
            }
            
            const totalPages = Math.ceil(containersToExport.length / rowsPerPage);
            
            const headerHTML = `
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${hexToRgba(primaryColor, 0.05)}">
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 22px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'}; ${rtlTextStyles}">
                            ${translations.containerReport || 'Container Report'}
                        </h1>
                        <div style="color: #666; font-size: 11px; font-weight: 500; margin-top: 3px; ${rtlTextStyles}">
                            ${businessInfo.businessname || user?.business_name || ''}
                        </div>
                        ${businessInfo.businessaddress ? `<div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">${businessInfo.businessaddress}</div>` : ''}
                        <div style="color: #888; font-size: 10px; margin-top: 4px; ${rtlTextStyles}">
                            ${currentShop?.shop_name ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name} | ` : ''}
                            Total: ${containersToExport.length} containers
                        </div>
                        <div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">
                            Generated on ${currentDateGlobal()} by ${user?.fullname || user?.username || 'System'}
                        </div>
                    </div>
                </div>
            `;

            const filterInfoHTML = (searchTerm || statusFilter || originFilter || destinationFilter || startDate || endDate) ? `
                <div style="margin: 8px 0; padding: 6px; background: #f9fafb; border-radius: 6px; font-size: 8px; color: #6b7280; text-align: center;">
                    ${translations.reportFilters || 'Applied Filters'}: 
                    ${searchTerm ? `Search: ${searchTerm}` : ''}
                    ${statusFilter ? ` | Status: ${statusFilter}` : ''}
                    ${originFilter ? ` | Origin: ${originFilter}` : ''}
                    ${destinationFilter ? ` | Destination: ${destinationFilter}` : ''}
                    ${startDate ? ` | From: ${startDate}` : ''}
                    ${endDate ? ` | To: ${endDate}` : ''}
                </div>
            ` : '';

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: ${pdfOrientation === 'portrait' ? '8px' : '7px'}; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle}">${translations.serialNo || 'No.'}</th>
                            <th style="${headerCellStyle}">${translations.containerNo || 'Container No'}</th>
                            <th style="${headerCellStyle}">${translations.containerType || 'Type'}</th>
                            <th style="${headerCellStyle}">${translations.vehicleNo || 'Vehicle'}</th>
                            <th style="${headerCellStyle}">${translations.driverName || 'Driver'}</th>
                            <th style="${headerCellStyle}">${translations.origin || 'Origin'}</th>
                            <th style="${headerCellStyle}">${translations.destination || 'Dest'}</th>
                            <th style="${headerCellStyle}">${translations.dispatchDate || 'Dispatch'}</th>
                            <th style="${headerCellStyle}">${translations.arrivalDate || 'Arrival'}</th>
                            <th style="${headerCellStyle}">${translations.status || 'Status'}</th>
                            <th style="${headerCellStyle.replace('border-right:', 'border: none;')}">${translations.addedBy || 'Added By'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, containersToExport.length);
                const pageContainers = containersToExport.slice(startRow, endRow);

                const tableRowsHTML = pageContainers.map((container, index) => {
                    const statusStyle = getStatusBadgeStyle(container.status);
                    const daysInTransit = container.dispatch_date && container.arrival_date 
                        ? Math.ceil((new Date(container.arrival_date) - new Date(container.dispatch_date)) / (1000 * 60 * 60 * 24))
                        : null;
                    
                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 5px; border: none; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; background: ${hexToRgba(primaryColor, 0.1)}; border-radius: 3px; font-size: 9px;">
                                    ${startRow + index + 1}
                                </div>
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; font-weight: 500; color: #111827; border-bottom: 1px solid #f0f0f0; font-size: 8px; ${rtlTextStyles}">
                                <div style="font-weight: 600;">${container.container_no || '-'}</div>
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${container.container_type || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${container.vehicle_no || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${container.driver_name || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${container.origin || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${container.destination || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #6b7280; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${formatDateGlobal(container.dispatch_date)}
                                ${daysInTransit ? `<div style="font-size: 6px; color: #9ca3af;">${daysInTransit}d</div>` : ''}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #6b7280; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${formatDateGlobal(container.arrival_date) || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-block; padding: 2px 6px; border-radius: 10px; background: ${statusStyle.backgroundColor}; color: ${statusStyle.color}; font-weight: 500; font-size: 7px;">
                                    ${getStatusLabelForPDF(container.status)}
                                </div>
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${container.created_by_username || '-'}
                            </td>
                        </tr>
                    `;
                }).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';
                
                const pageHTML = `
                    <div style="width: 800px; padding: 12px 20px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        ${filterInfoHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '800px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    onclone: (clonedDoc) => {
                        if (isRTL) {
                            const elements = clonedDoc.querySelectorAll('div, span, td, th, h1');
                            elements.forEach(el => {
                                el.style.unicodeBidi = 'plaintext';
                                el.style.textRendering = 'optimizeLegibility';
                                el.style.fontFeatureSettings = '"calt", "liga", "kern"';
                            });
                        }
                    }
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                addPatternBackground(pdf);
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }

            const pageSizeText = pdfPageSize === 'a4' ? 'A4' : pdfPageSize === 'letter' ? 'Letter' : pdfPageSize === 'legal' ? 'Legal' : 'A5';
            const orientationText = pdfOrientation === 'portrait' ? 'Portrait' : 'Landscape';
            const fileName = `containers_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'report'}_${pageSizeText}_${orientationText}_${currentDateGlobal()}.pdf`;
            
            pdf.save(fileName);
            
            showToast('success', `PDF exported successfully with ${containersToExport.length} container records across ${totalPages} pages!`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredContainers, currentLanguage, currentTheme, pdfOrientation, pdfPageSize, pdfPageRange, pdfStartPage, pdfEndPage, translations, businessInfo, currentShop, user, showToast, getRowsPerPage, statusFilter, originFilter, destinationFilter, startDate, endDate, searchTerm]);

    const handleView = useCallback((container) => {
        setSelectedContainer(container);
        setShowViewModal(true);
    }, []);

    const handleEdit = useCallback((container) => {
        if (container.created_by_username !== user?.username) {
            showToast('error', 'You can only edit your own containers');
            return;
        }

        setSelectedContainer(container);
        setEditFormData({
            container_no: container.container_no || '',
            container_type: container.container_type || '',
            vehicle_no: container.vehicle_no || '',
            driver_name: container.driver_name || '',
            origin: container.origin || '',
            destination: container.destination || '',
            dispatch_date: container.dispatch_date || '',
            arrival_date: container.arrival_date || '',
            status: container.status || '',
            remarks: container.remarks || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    }, [user, showToast]);

    const handleDeleteClick = useCallback((containerId) => {
        const container = containers.find(c => c.id === containerId);
        if (!container || container.created_by_username !== user?.username) {
            showToast('error', 'You can only delete your own containers');
            return;
        }

        setContainerToDelete(containerId);
        setShowDeleteModal(true);
    }, [containers, user, showToast]);

    const handleDelete = useCallback(async () => {
        if (!containerToDelete || !currentShop) return;

        const container = containers.find(c => c.id === containerToDelete);
        if (!container || container.created_by_username !== user?.username) {
            showToast('error', 'You can only delete your own containers');
            setShowDeleteModal(false);
            setContainerToDelete(null);
            return;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/containers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: containerToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Container deleted successfully');
                fetchContainers();
                setShowDeleteModal(false);
                setContainerToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting container');
            }
        } catch (error) {
            console.error('Error deleting container:', error);
            showToast('error', translations.deleteError || 'Error deleting container');
        }
    }, [containerToDelete, currentShop, containers, user, translations, showToast, fetchContainers]);

    const handleUpdateContainer = useCallback(async (e) => {
        e.preventDefault();

        if (!selectedContainer || !currentShop) return;

        const errors = {};
        if (!editFormData.container_no.trim()) {
            errors.container_no = translations.containerNoRequired || 'Container number is required';
        }

        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/containers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedContainer.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    ...editFormData
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.containerUpdated || 'Container updated successfully');
                setShowEditModal(false);
                fetchContainers();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating container');
            }
        } catch (error) {
            console.error('Error updating container:', error);
            showToast('error', translations.updateError || 'Error updating container');
        } finally {
            setEditLoading(false);
        }
    }, [selectedContainer, currentShop, editFormData, user, translations, showToast, fetchContainers]);

    const handlePageChange = useCallback((page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setCurrentPage(page);
        }
    }, [pagination.totalPages]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    }, []);

    const getStatusBadge = useCallback((status) => {
        switch (status) {
            case 'pending':
                return { bg: '#f59e0b20', color: '#f59e0b', border: '#f59e0b40', icon: faClock };
            case 'in_transit':
                return { bg: '#3b82f620', color: '#3b82f6', border: '#3b82f640', icon: faTruck };
            case 'arrived':
                return { bg: '#10b98120', color: '#10b981', border: '#10b98140', icon: faCheckDouble };
            case 'delayed':
                return { bg: '#ef444420', color: '#ef4444', border: '#ef444440', icon: faHourglassHalf };
            case 'completed':
                return { bg: '#8b5cf620', color: '#8b5cf6', border: '#8b5cf640', icon: faCheckCircle };
            default:
                return { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faBox };
        }
    }, []);

    const getStatusLabel = useCallback((status) => {
        switch (status) {
            case 'pending':
                return translations.pending || 'Pending';
            case 'in_transit':
                return translations.inTransit || 'In Transit';
            case 'arrived':
                return translations.arrived || 'Arrived';
            case 'delayed':
                return translations.delayed || 'Delayed';
            case 'completed':
                return translations.completed || 'Completed';
            default:
                return status?.replace('_', ' ') || '-';
        }
    }, [translations]);

    const calculateDaysInTransit = useCallback((dispatchDate, arrivalDate) => {
        if (!dispatchDate || !arrivalDate) return '-';
        const dispatch = new Date(dispatchDate);
        const arrival = new Date(arrivalDate);
        if (isNaN(dispatch) || isNaN(arrival)) return '-';
        const diffTime = Math.abs(arrival - dispatch);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    }, []);

    const getTodayDate = useCallback(() => {
        return new Date().toISOString().split('T')[0];
    }, []);

    // Handle view picture in new tab
    const handleViewPicture = useCallback((pictureUrl) => {
        if (pictureUrl) {
            window.open(pictureUrl, '_blank');
        } else {
            showToast('warning', translations.noPicture || 'No picture available');
        }
    }, [translations, showToast]);

    // Render shop indicator
    const renderShopIndicator = useCallback(() => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    }, [currentShop, translations]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faBox} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.containerList || 'Containers List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allContainers || 'My Containers'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>
                {renderShopIndicator()}
            </div>

            {/* Summary Cards */}
            {currentShop && filteredContainers.length > 0 && (
                <div className="summary-cards-container" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '15px',
                    margin: '20px 0'
                }}>
                    <div className="summary-card" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', padding: '15px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.totalContainers || 'Total'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totals.total}</div>
                    </div>
                    <div className="summary-card" style={{ background: '#f59e0b20', border: '1px solid #f59e0b40', color: '#f59e0b', padding: '15px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.pending || 'Pending'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totals.pending}</div>
                    </div>
                    <div className="summary-card" style={{ background: '#3b82f620', border: '1px solid #3b82f640', color: '#3b82f6', padding: '15px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.inTransit || 'In Transit'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totals.inTransit}</div>
                    </div>
                    <div className="summary-card" style={{ background: '#10b98120', border: '1px solid #10b98140', color: '#10b981', padding: '15px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.arrived || 'Arrived'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totals.arrived}</div>
                    </div>
                    <div className="summary-card" style={{ background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', padding: '15px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.delayed || 'Delayed'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totals.delayed}</div>
                    </div>
                    <div className="summary-card" style={{ background: '#8b5cf620', border: '1px solid #8b5cf640', color: '#8b5cf6', padding: '15px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.completed || 'Completed'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totals.completed}</div>
                    </div>
                </div>
            )}

            {/* Advanced Filters Section with PDF Options in same row */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="containerNo">{translations.containerNoFilter || 'Container Number'}</option>
                            <option value="vehicleNo">{translations.vehicleNoFilter || 'Vehicle Number'}</option>
                            <option value="driverName">{translations.driverNameFilter || 'Driver Name'}</option>
                            <option value="status">{translations.statusFilter || 'Status'}</option>
                            <option value="origin">{translations.originFilter || 'Origin'}</option>
                            <option value="destination">{translations.destinationFilter || 'Destination'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'containerNo' && (
                        <div className="filter-group compact">
                            <input type="text" value={containerNoFilter} onChange={(e) => setContainerNoFilter(e.target.value)} placeholder={translations.containerNoFilter || 'Container number...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}

                    {filterBy === 'vehicleNo' && (
                        <div className="filter-group compact">
                            <input type="text" value={vehicleNoFilter} onChange={(e) => setVehicleNoFilter(e.target.value)} placeholder={translations.vehicleNoFilter || 'Vehicle number...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}

                    {filterBy === 'driverName' && (
                        <div className="filter-group compact">
                            <input type="text" value={driverNameFilter} onChange={(e) => setDriverNameFilter(e.target.value)} placeholder={translations.driverNameFilter || 'Driver name...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}

                    {filterBy === 'status' && (
                        <div className="filter-group compact">
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                {statusOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'origin' && (
                        <div className="filter-group compact">
                            <input type="text" value={originFilter} onChange={(e) => setOriginFilter(e.target.value)} placeholder={translations.originFilter || 'Origin...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}

                    {filterBy === 'destination' && (
                        <div className="filter-group compact">
                            <input type="text" value={destinationFilter} onChange={(e) => setDestinationFilter(e.target.value)} placeholder={translations.destinationFilter || 'Destination...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input ref={startDateRef} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={getTodayDate()} className="filter-input compact date-input" title={translations.startDate || 'Start Date'} />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input ref={endDateRef} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={getTodayDate()} className="filter-input compact date-input" title={translations.endDate || 'End Date'} />
                            </div>
                        </>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input type="text" value={addedByFilter} onChange={(e) => setAddedByFilter(e.target.value)} placeholder={translations.addedByFilter || 'Added by username...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input type="text" placeholder={translations.searchPlaceholder || 'Search containers...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button onClick={fetchContainers} className="refresh-btn-modern compact" disabled={loading} title={translations.refresh || 'Refresh'}>
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    {/* PDF Orientation Dropdown */}
                    <div className="filter-group compact">
                        <select 
                            value={pdfOrientation} 
                            onChange={(e) => setPdfOrientation(e.target.value)} 
                            className="filter-select compact"
                            title={translations.orientation || 'Orientation'}
                            style={{ minWidth: '100px' }}
                        >
                            <option value="portrait">{translations.portrait || 'Portrait'}</option>
                            <option value="landscape">{translations.landscape || 'Landscape'}</option>
                        </select>
                    </div>

                    {/* PDF Page Size Dropdown */}
                    <div className="filter-group compact">
                        <select 
                            value={pdfPageSize} 
                            onChange={(e) => setPdfPageSize(e.target.value)} 
                            className="filter-select compact"
                            title={translations.pageSize || 'Page Size'}
                            style={{ minWidth: '90px' }}
                        >
                            <option value="a4">{translations.a4 || 'A4'}</option>
                            <option value="letter">{translations.letter || 'Letter'}</option>
                            <option value="legal">{translations.legal || 'Legal'}</option>
                            <option value="a5">{translations.a5 || 'A5'}</option>
                        </select>
                    </div>

                    {/* PDF Page Range Dropdown */}
                    <div className="filter-group compact">
                        <select 
                            value={pdfPageRange} 
                            onChange={(e) => setPdfPageRange(e.target.value)} 
                            className="filter-select compact"
                            title="Page Range"
                            style={{ minWidth: '110px' }}
                        >
                            <option value="all">All Pages</option>
                            <option value="range">Page Range</option>
                        </select>
                    </div>

                    {/* Page Range Inputs (only show when range is selected) */}
                    {pdfPageRange === 'range' && (
                        <>
                            <div className="filter-group compact" style={{ width: '70px' }}>
                                <input 
                                    type="number" 
                                    min="1" 
                                    value={pdfStartPage} 
                                    onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="filter-input compact"
                                    placeholder="From"
                                    style={{ textAlign: 'center' }}
                                />
                            </div>
                            <div className="filter-group compact" style={{ width: '70px' }}>
                                <input 
                                    type="number" 
                                    min={pdfStartPage} 
                                    value={pdfEndPage} 
                                    onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))}
                                    className="filter-input compact"
                                    placeholder="To"
                                    style={{ textAlign: 'center' }}
                                />
                            </div>
                        </>
                    )}

                    {/* Export PDF Button */}
                    <div className="filter-group compact">
                        <button 
                            onClick={exportToPDF} 
                            className="refresh-btn-modern compact" 
                            disabled={filteredContainers.length === 0 || pdfLoading} 
                            title={translations.exportToPDF || 'Export to PDF'}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                        >
                            {pdfLoading ? (
                                <div className="btn-spinner-small"></div>
                            ) : (
                                <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            <span className="btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading your container data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view containers'}</p>
                        <button className="btn-modern btn-primary-modern" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.containerNo || 'Container No'}</th>
                                            <th className="centered-cell">{translations.containerType || 'Type'}</th>
                                            <th className="centered-cell">{translations.vehicleNo || 'Vehicle'}</th>
                                            <th className="centered-cell">{translations.driverName || 'Driver'}</th>
                                            <th className="centered-cell">{translations.origin || 'Origin'}</th>
                                            <th className="centered-cell">{translations.destination || 'Destination'}</th>
                                            <th className="centered-cell">{translations.dispatchDate || 'Dispatch'}</th>
                                            <th className="centered-cell">{translations.arrivalDate || 'Arrival'}</th>
                                            <th className="centered-cell">{translations.status || 'Status'}</th>
                                            <th className="centered-cell">{translations.remarks || 'Remarks'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentContainers.map((container, index) => {
                                            const addedBy = container.created_by_username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            const statusStyle = getStatusBadge(container.status);
                                            const daysInTransit = calculateDaysInTransit(container.dispatch_date, container.arrival_date);
                                            const hasPicture = container.conveyance_picture && container.conveyance_picture !== '';

                                            return (
                                                <tr key={container.id}>
                                                    <td className="id-cell centered-cell"><span className="cell-text">{pagination.startIndex + index + 1}</span></td>
                                                    <td className="centered-cell"><span className="cell-text container-number">{container.container_no || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{container.container_type || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{container.vehicle_no || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{container.driver_name || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{container.origin || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{container.destination || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{formatDate(container.dispatch_date)}</span>{daysInTransit !== '-' && (<small className="days-badge">{daysInTransit}d</small>)}</td>
                                                    <td className="centered-cell"><span className="cell-text">{formatDate(container.arrival_date)}</span></td>
                                                    <td className="centered-cell"><div className="status-badge" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}><FontAwesomeIcon icon={statusStyle.icon} size="xs" />{getStatusLabel(container.status)}</div></td>
                                                    <td className="centered-cell"><span className="cell-text remarks-cell" title={container.remarks}>{container.remarks?.substring(0, 20) || '-'}{container.remarks?.length > 20 && '...'}</span></td>
                                                    <td className="centered-cell"><span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`}><span className="cell-text">{addedBy}</span></span></td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="actions-content" style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                                            {hasPicture && (
                                                                <button onClick={() => handleViewPicture(container.conveyance_picture)} className="action-btn-modern picture-btn-modern" title={translations.viewPicture || 'View Picture'} style={{ background: '#10b98120', color: '#10b981', width: '32px', height: '32px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                                                                    <FontAwesomeIcon icon={faImage} />
                                                                </button>
                                                            )}
                                                            <button onClick={() => handleEdit(container)} className="action-btn-modern edit-btn-modern" title={translations.edit || 'Edit'} disabled={container.created_by_username !== user?.username} style={{ background: '#8b5cf620', color: '#8b5cf6', width: '32px', height: '32px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button onClick={() => handleDeleteClick(container.id)} className="action-btn-modern delete-btn-modern" title={translations.delete || 'Delete'} disabled={container.created_by_username !== user?.username} style={{ background: '#ef444420', color: '#ef4444', width: '32px', height: '32px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredContainers.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info"><span>{translations.showing || 'Showing'} {pagination.startIndex + 1}-{Math.min(pagination.endIndex, filteredContainers.length)} {translations.of || 'of'} {filteredContainers.length} {translations.containers || 'containers'}</span>{currentShop?.shop_name && (<span className="shop-info-pagination"> | {translations.shopName || 'Shop'}: {currentShop.shop_name}</span>)}</div>
                            <div className="pagination-controls">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn"><FontAwesomeIcon icon={faChevronLeft} /><span>{translations.previous || 'Previous'}</span></button>
                                <div className="page-numbers">{Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => { let pageNum; if (pagination.totalPages <= 5) { pageNum = i + 1; } else if (currentPage <= 3) { pageNum = i + 1; } else if (currentPage >= pagination.totalPages - 2) { pageNum = pagination.totalPages - 4 + i; } else { pageNum = currentPage - 2 + i; } return (<button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>); })}</div>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="pagination-btn"><span>{translations.next || 'Next'}</span><FontAwesomeIcon icon={faChevronRight} /></button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal - Does NOT close on outside click */}
            {showViewModal && selectedContainer && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern"><div className="modal-icon"><FontAwesomeIcon icon={faBox} /></div><div className="modal-title"><h3>{translations.containerDetails || 'Container Details'}</h3><span className="container-number-badge">#{selectedContainer.container_no}</span></div><button className="close-btn-modern" onClick={() => setShowViewModal(false)}><FontAwesomeIcon icon={faTimes} /></button></div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faBox} />Container Summary</h4><div className="view-grid"><div className="view-row"><span className="view-label">{translations.containerId || 'Container ID'}:</span><span className="view-value">#{selectedContainer.id}</span></div><div className="view-row"><span className="view-label">{translations.containerNo || 'Container Number'}:</span><span className="view-value">{selectedContainer.container_no || '-'}</span></div><div className="view-row"><span className="view-label">{translations.containerType || 'Container Type'}:</span><span className="view-value">{selectedContainer.container_type || '-'}</span></div><div className="view-row"><span className="view-label">{translations.status || 'Status'}:</span><span className="view-value"><div className="status-badge" style={getStatusBadge(selectedContainer.status)}><FontAwesomeIcon icon={getStatusBadge(selectedContainer.status).icon} />{getStatusLabel(selectedContainer.status)}</div></span></div></div></div>
                            <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faMapMarkedAlt} />{translations.routeInfo || 'Route Information'}</h4><div className="view-grid"><div className="view-row"><span className="view-label">{translations.origin || 'Origin'}:</span><span className="view-value">{selectedContainer.origin || '-'}</span></div><div className="view-row"><span className="view-label">{translations.destination || 'Destination'}:</span><span className="view-value">{selectedContainer.destination || '-'}</span></div></div></div>
                            <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faTruck} />{translations.driverInfo || 'Driver Information'}</h4><div className="view-grid"><div className="view-row"><span className="view-label">{translations.vehicleNo || 'Vehicle Number'}:</span><span className="view-value">{selectedContainer.vehicle_no || '-'}</span></div><div className="view-row"><span className="view-label">{translations.driverName || 'Driver Name'}:</span><span className="view-value">{selectedContainer.driver_name || '-'}</span></div></div></div>
                            <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faClockIcon} />{translations.timelineInfo || 'Timeline Information'}</h4><div className="view-grid"><div className="view-row"><span className="view-label">{translations.dispatchDate || 'Dispatch Date'}:</span><span className="view-value">{formatDate(selectedContainer.dispatch_date) || '-'}</span></div><div className="view-row"><span className="view-label">{translations.arrivalDate || 'Arrival Date'}:</span><span className="view-value">{formatDate(selectedContainer.arrival_date) || '-'}</span></div>{selectedContainer.dispatch_date && selectedContainer.arrival_date && (<div className="view-row"><span className="view-label">{translations.daysInTransit || 'Days in Transit'}:</span><span className="view-value">{calculateDaysInTransit(selectedContainer.dispatch_date, selectedContainer.arrival_date)} days</span></div>)}</div></div>
                            {selectedContainer.remarks && (<div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faComment} />{translations.remarks || 'Remarks'}</h4><div className="remarks-box">{selectedContainer.remarks}</div></div>)}
                            
                            {/* Conveyance Picture Section */}
                            {selectedContainer.conveyance_picture && (
                                <div className="view-section">
                                    <h4 className="view-section-title">
                                        <FontAwesomeIcon icon={faCamera} />
                                        {translations.conveyancePicture || 'Conveyance Picture'}
                                    </h4>
                                    <div className="conveyance-picture-container" style={{ textAlign: 'center', marginTop: '10px' }}>
                                        <img 
                                            src={selectedContainer.conveyance_picture} 
                                            alt="Conveyance" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '300px', 
                                                borderRadius: '8px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                cursor: 'pointer'
                                            }} 
                                            onClick={() => handleViewPicture(selectedContainer.conveyance_picture)}
                                        />
                                        <div style={{ marginTop: '8px' }}>
                                            <button 
                                                className="btn-modern btn-primary-modern" 
                                                onClick={() => handleViewPicture(selectedContainer.conveyance_picture)}
                                                style={{ padding: '5px 15px', fontSize: '12px' }}
                                            >
                                                <FontAwesomeIcon icon={faImage} /> Open in New Tab
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="view-section metadata-section"><div className="view-grid"><div className="view-row"><span className="view-label">{translations.addedBy || 'Added By'}:</span><span className="view-value"><span className="added-by-badge">{selectedContainer.created_by_username || '-'}</span></span></div><div className="view-row"><span className="view-label">{translations.dateAdded || 'Date Added'}:</span><span className="view-value">{formatDate(selectedContainer.created_at) || formatDate(selectedContainer.dispatch_date)}</span></div>{selectedContainer.updated_at && (<div className="view-row"><span className="view-label">{translations.lastUpdated || 'Last Updated'}:</span><span className="view-value">{formatDate(selectedContainer.updated_at)}</span></div>)}{selectedContainer.shop_name && (<div className="view-row"><span className="view-label">{translations.shopInfo || 'Shop'}:</span><span className="view-value">{selectedContainer.shop_name} {selectedContainer.shop_code ? `(${selectedContainer.shop_code})` : ''}</span></div>)}</div></div>
                        </div>
                        <div className="modal-footer-modern"><button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>{selectedContainer.created_by_username === user?.username && (<button className="btn-modern btn-primary-modern" onClick={() => { setShowViewModal(false); handleEdit(selectedContainer); }}><FontAwesomeIcon icon={faEdit} />{translations.edit || 'Edit'}</button>)}</div>
                    </div>
                </div>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE, Does NOT close on outside click */}
            {showEditModal && selectedContainer && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div 
                        ref={modalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDrag}>
                            <div className="modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="modal-title">
                                <h3>{translations.editContainer || 'Edit Container'}</h3>
                                <span className="container-number-badge">#{selectedContainer.container_no}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setWidthMode(key)}
                                        title={mode.label}
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleUpdateContainer}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className={`form-group ${editFormErrors.container_no ? 'has-error' : ''}`}>
                                        <label htmlFor="edit_container_no">{translations.containerNo || 'Container No'} <span className="required">*</span></label>
                                        <input type="text" id="edit_container_no" value={editFormData.container_no} onChange={(e) => setEditFormData({...editFormData, container_no: e.target.value})} className={editFormErrors.container_no ? 'error' : ''} dir={isRTL ? 'rtl' : 'ltr'} />
                                        {editFormErrors.container_no && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.container_no}</div>)}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_container_type">{translations.containerType || 'Container Type'}</label>
                                        <select id="edit_container_type" value={editFormData.container_type} onChange={(e) => setEditFormData({...editFormData, container_type: e.target.value})} dir={isRTL ? 'rtl' : 'ltr'}>
                                            <option value="">Select Type</option>
                                            {containerTypeOptions.map(type => (<option key={type} value={type}>{type}</option>))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_vehicle_no">{translations.vehicleNo || 'Vehicle No'}</label>
                                        <input type="text" id="edit_vehicle_no" value={editFormData.vehicle_no} onChange={(e) => setEditFormData({...editFormData, vehicle_no: e.target.value})} dir={isRTL ? 'rtl' : 'ltr'} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_driver_name">{translations.driverName || 'Driver Name'}</label>
                                        <input type="text" id="edit_driver_name" value={editFormData.driver_name} onChange={(e) => setEditFormData({...editFormData, driver_name: e.target.value})} dir={isRTL ? 'rtl' : 'ltr'} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_origin">{translations.origin || 'Origin'}</label>
                                        <input type="text" id="edit_origin" value={editFormData.origin} onChange={(e) => setEditFormData({...editFormData, origin: e.target.value})} dir={isRTL ? 'rtl' : 'ltr'} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_destination">{translations.destination || 'Destination'}</label>
                                        <input type="text" id="edit_destination" value={editFormData.destination} onChange={(e) => setEditFormData({...editFormData, destination: e.target.value})} dir={isRTL ? 'rtl' : 'ltr'} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_dispatch_date">{translations.dispatchDate || 'Dispatch Date'}</label>
                                        <input type="date" id="edit_dispatch_date" value={editFormData.dispatch_date} onChange={(e) => setEditFormData({...editFormData, dispatch_date: e.target.value})} max={getTodayDate()} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_arrival_date">{translations.arrivalDate || 'Arrival Date'}</label>
                                        <input type="date" id="edit_arrival_date" value={editFormData.arrival_date} onChange={(e) => setEditFormData({...editFormData, arrival_date: e.target.value})} min={editFormData.dispatch_date} max={getTodayDate()} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit_status">{translations.status || 'Status'}</label>
                                        <select id="edit_status" value={editFormData.status} onChange={(e) => setEditFormData({...editFormData, status: e.target.value})} dir={isRTL ? 'rtl' : 'ltr'}>
                                            {statusOptions.filter(opt => opt.value).map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                                        </select>
                                    </div>
                                    <div className="form-group full-width">
                                        <label htmlFor="edit_remarks">{translations.remarks || 'Remarks'}</label>
                                        <textarea id="edit_remarks" value={editFormData.remarks} onChange={(e) => setEditFormData({...editFormData, remarks: e.target.value})} rows="3" dir={isRTL ? 'rtl' : 'ltr'}></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)}>{translations.cancel || 'Cancel'}</button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? (<><div className="btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>) : (<><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal - Does NOT close on outside click */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="modal-title"><h3 style={{ color: 'var(--customer-error-color)' }}>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="close-btn-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>{translations.deleteMessage || 'Are you sure you want to delete this container?'}</p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                            <button type="button" className="btn-modern btn-delete-modern" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContainerList;